<script lang="ts">
	/**
	 * What a purchase ends on: a message and the links to go on to — the documentation, the licence
	 * portal, a download (§14). Authored on a product or on its family; the product's own replaces
	 * the family's outright.
	 *
	 * A link is `https://…`, `http://…` or a path on this store starting with `/`, checked here as the
	 * server checks it, so a link the server would refuse is flagged on its row rather than at Save.
	 * Bind `valid` to gate the surrounding form.
	 */

	import { Button, Input, Textarea } from 'flowbite-svelte';
	import { isPostPurchaseURL, type PostPurchaseDraft } from '../Utilities';

	export let value: PostPurchaseDraft = { message: '', links: [] };
	export let valid: boolean = true;
	export let disabled: boolean = false;
	export let id: string = 'post-purchase';

	$: rowErrors = value.links.map(link => {
		const label = link.label.trim();
		const url = link.url.trim();
		if (label === '' && url === '') return '';
		if (label === '') return 'Give the link a label.';
		if (!isPostPurchaseURL(url)) return 'An https:// address, or a path on this store starting with /.';
		return '';
	});
	$: valid = rowErrors.every(error => error === '');

	function fnAdd(): void {
		value = { ...value, links: [...value.links, { label: '', url: '' }] };
	}

	function fnRemove(index: number): void {
		value = { ...value, links: value.links.filter((_unused, position) => position !== index) };
	}
</script>

<div class="space-y-3">
	<Textarea
		id="{id}-message"
		rows={2}
		placeholder="Thank you! Your licence is ready — here is where to start."
		bind:value={value.message}
		{disabled}
	/>
	<p class="text-xs text-default">
		HTML is allowed: paragraphs, bold, lists and links. Scripts, styles and other markup are removed when the message is shown.
	</p>

	{#each value.links as link, index (index)}
		<div>
			<div class="grid gap-2 sm:grid-cols-[12rem_1fr_auto] items-center">
				<Input id="{id}-label-{index}" size="sm" placeholder="Documentation" aria-label="Link label" bind:value={link.label} {disabled} />
				<Input
					id="{id}-url-{index}"
					size="sm"
					placeholder="https://docs.example.com"
					aria-label="Link address"
					aria-invalid={rowErrors[index] !== ''}
					bind:value={link.url}
					{disabled}
				/>
				<Button color="none" class="!p-2 text-default hover:text-red-600" aria-label="Remove {link.label || 'this link'}" {disabled} on:click={() => fnRemove(index)}>
					<i class="fa-solid fa-xmark" aria-hidden="true" />
				</Button>
			</div>
			{#if rowErrors[index]}
				<p class="text-xs text-red-600 dark:text-red-400 mt-1">{rowErrors[index]}</p>
			{/if}
		</div>
	{/each}

	<Button color="alternative" size="xs" {disabled} on:click={fnAdd}>
		<i class="fa-solid fa-plus mr-2" aria-hidden="true" />Add a link
	</Button>
</div>
